<template>
  <div class="space-y-1">
    <EntitySelector v-if="!inFlowGraph" v-model="entitySelection" :settings="settings" />
    <p v-if="isDeprecatedTemplateSource" class="text-xs text-amber-400 mt-1">
      This binding uses the deprecated Template source. Select a concrete Home Assistant entity.
    </p>
    <BuilderSelect class="w-full text-sm px-1 py-1" v-model="binding.attribute">
      <option value="">state (default)</option>
      <option v-for="s in attributeSuggestions" :key="s.attribute" :value="s.attribute">
        {{ s.label }}
      </option>
    </BuilderSelect>

    <!-- Value transforms — editor-only pipeline applied to the incoming
         value before strategy evaluation (preview only, see
         valueTransforms.js). Strategy-agnostic — see
         CollapsibleTransformsSection.vue, shared with StateBindingEditor.vue. -->
    <CollapsibleTransformsSection :pipeline="binding.transforms" />

    <!-- Simplified on/off layout — shown only while the binding holds exactly
         the historical two-rule on/off shape. Adding a rule switches to the
         generic list below; this is a display choice only, rules[] is the
         same data either way. -->
    <div v-if="isSimpleToggle" class="space-y-2">
      <div class="flex items-center gap-2">
        <span class="text-xs text-gray-400 w-10">On</span>
        <PropertyEditorFactory class="flex-1" stringBuilder :builderVariables="builderVariables" :valueExpression="incomingValueExpression"
          :editorMeta="editorMeta" :modelValue="onValue"
          @update:modelValue="v => onValue = v" />
      </div>
      <div class="flex items-center gap-2">
        <span class="text-xs text-gray-400 w-10">Off</span>
        <PropertyEditorFactory class="flex-1" stringBuilder :builderVariables="builderVariables" :valueExpression="incomingValueExpression"
          :editorMeta="editorMeta" :modelValue="offValue"
          @update:modelValue="v => offValue = v" />
      </div>
      <button type="button" class="text-xs text-cyan-400 hover:text-cyan-300" @click="addRule">
        + Add rule
      </button>
    </div>

    <!-- Generic rule list — any number of rules, evaluated top to bottom. -->
    <div v-else class="space-y-1.5">
      <div v-for="(rule, index) in binding.rules" :key="index" :ref="el => setRowRef(index, el)">
        <MappingRuleRow
          :rule="rule" :editorMeta="editorMeta" :builderVariables="builderVariables"
          :valueExpression="incomingValueExpression"
          :canBeDefault="index === binding.rules.length - 1"
          :canMoveUp="canMoveUp(index)" :canMoveDown="canMoveDown(index)"
          :issues="issuesForRule(index)"
          @move-up="moveRule(index, -1)" @move-down="moveRule(index, 1)"
          @remove="removeRule(index)" />
      </div>
      <button type="button" class="text-xs text-cyan-400 hover:text-cyan-300" @click="addRule">
        + Add rule
      </button>

      <!-- Design-time only — lives in BindingEditorState, never on
           `binding`, see the component doc comment below. -->
      <div v-if="editorMeta?.editor === 'color'" class="flex items-center gap-2 text-xs text-gray-400">
        <BuilderCheckbox label="Enable interpolation" v-model="editorState.interpolationEnabled" />
        <span v-if="editorState.interpolationEnabled && insufficientAnchors" class="text-yellow-400">
          ⚠ Interpolation has insufficient anchor points.
        </span>
      </div>
    </div>

    <!-- Preview & Validation — merged into one drawer (was two separate,
         always-visible sections). Collapsed unless there's an existing
         validation issue worth surfacing immediately. Neither
         BindingPreview.vue nor BindingValidationSummary.vue changed at
         all — only what wraps them here did. -->
    <div class="border-t border-slate-700 pt-2">
      <button type="button" class="flex items-center gap-1.5 text-xs text-cyan-400 hover:text-cyan-300"
        :aria-expanded="testOpen" @click="testOpen = !testOpen">
        <span class="inline-block transition-transform" :class="{ 'rotate-90': testOpen }">▸</span>
        Preview &amp; Validation
        <span v-if="issues.length" class="text-yellow-400 font-normal">({{ issues.length }})</span>
      </button>
      <div v-if="testOpen" class="mt-2 space-y-2">
        <BindingPreview :binding="binding" :editorMeta="editorMeta" :editorState="editorState"
          :valueExpression="incomingValueExpression" />
        <BindingValidationSummary :issues="issues" @select-rule="scrollToRule" />
      </div>
    </div>
  </div>
</template>

<script setup>
/**
 * MappingBindingEditor.vue
 *
 * Editor for the 'mapping' binding strategy — a full rules[] editor.
 * Every rule is { when: {operator, value}|null, result }, evaluated top
 * to bottom; a rule with when: null is the default and must be last (see
 * docs/Binding_Architecture_FINAL.md §3). Only the last row may become
 * the default — reordering (moveRule) never lets a non-default rule move
 * past a trailing default rule, so that invariant holds structurally,
 * without a separate validation pass.
 *
 * Presents a simplified on/off layout (via the deprecated-looking but
 * still-current onValue/offValue helpers below, which operate on
 * binding.rules directly, not AutomationBinding's compatibility
 * accessors) while the binding holds exactly that two-rule shape; adding
 * a rule reveals the generic list. Purely a display choice — rules[] is
 * the only data model, in both layouts.
 *
 * Resolved and rendered by BindingEditorRouter.vue; never referenced by
 * name from StateBindingsTable. No generator or migration changes were
 * needed — bindingGenerators.js's generateMapping already evaluates an
 * arbitrary rules[] list.
 *
 * Also renders BindingPreview.vue below the rule list/simplified layout —
 * a live, local (no Jinja2/generator) preview of which rule matches a
 * typed test value — and BindingValidationSummary.vue, driven by
 * validateMappingRules.js (a separate utility from evaluateMappingRules.js;
 * one checks the rule set, the other evaluates one value against it).
 * Per-rule issues are sliced from the same validation pass and handed to
 * each MappingRuleRow as a display-only prop.
 *
 * "Enable interpolation" (color mappings only) lives in
 * editorState.interpolationEnabled (see BindingEditorState.js) — an
 * editor-only, design-time preview toggle. It is never read from or
 * written to `binding`/AutomationBinding, never serialized, and does not
 * change the rule model, generator, or migration in any way; it only
 * changes what BindingPreview.vue displays (see interpolateColor.js).
 *
 * TransformEditor.vue edits binding.transforms directly — an ordered,
 * strategy-agnostic value-transform pipeline (see valueTransforms.js).
 * Unlike interpolation/preview-value, this is part of the persisted
 * AutomationBinding model, not BindingEditorState — see AutomationBinding.js
 * and "Architecture Correction — Move Value Transforms into
 * AutomationBinding". BindingPreview.vue runs it over
 * editorState.previewValue before evaluating the mapping rules. No
 * generator change was made — transform generation is a later feature.
 *
 * "Value Transforms" and "Preview & Validation" are collapsed by default —
 * pure local UI visibility state, not part of BindingEditorState, since
 * it's fine for a fresh mount to always start collapsed rather than
 * remember across sessions. Each starts open only when there's already
 * something to show (a configured transform pipeline, or an existing
 * validation issue) — see "UX Refactoring Phase 1 — Progressive
 * Disclosure". Value Transforms' own open/closed state now lives inside
 * CollapsibleTransformsSection.vue (shared with StateBindingEditor.vue);
 * `testOpen` below still belongs to this file since Preview & Validation
 * isn't shared.
 */
import { computed, ref } from 'vue';
import { getPropertyEditorMeta } from '../../../automation/schema/PropertyEditorMeta.js';
import { getAttributeSuggestions } from '../../../utils/hassStateAttributes.js';
import { buildIncomingValueExpression } from '../../../automation/generators/bindingGenerators.js';
import { INCOMING_VALUE_TOKEN } from '../../../automation/schema/incomingValueToken.js';
import { validateMappingRules } from '../../../automation/schema/validateMappingRules.js';
import { getColorAnchors } from '../../../automation/schema/interpolateColor.js';
import PropertyEditorFactory from './PropertyEditorFactory.vue';
import EntitySelector from '../EntitySelector.vue';
import MappingRuleRow from './MappingRuleRow.vue';
import BindingPreview from './BindingPreview.vue';
import BindingValidationSummary from './BindingValidationSummary.vue';
import CollapsibleTransformsSection from './CollapsibleTransformsSection.vue';
import BuilderSelect from './BuilderSelect.vue';
import BuilderCheckbox from './BuilderCheckbox.vue';

const props = defineProps({
  binding:     { type: Object, required: true }, // AutomationBinding — mutated directly
  node:        { type: Object, required: true },
  settings:    { type: Object, required: true },
  editorState: { type: Object, required: true },  // BindingEditorState — transient UI state only
  // In a Home Assistant flow graph the entity comes from the binding node's wire.
  inFlowGraph: { type: Boolean, default: false },
});

const isDeprecatedTemplateSource = computed(() => props.binding.sourceType === 'template');
const entitySelection = computed({
  get: () => props.binding.entityId,
  set(value) {
    props.binding.entityId = value;
    props.binding.sourceType = 'entity';
  },
});
const bindingEntityId = computed(() => props.binding.entityId);

const attributeSuggestions = computed(() => getAttributeSuggestions(props.binding.property, bindingEntityId.value));
const editorMeta = computed(() => getPropertyEditorMeta(props.node.schemaV2, props.binding.property));

// ── String Builder variables ────────────────────────────────────────────────
//
// The one variable a mapping result has that a plain property value doesn't:
// the value the mapping just matched on. It is offered as the readable
// `{{value}}` pill — the same alias the result fields display — while
// buildIncomingValueExpression() supplies what it means in the model. That
// function is the single source of truth for this expression: it is exactly
// what the rules' own conditions compare against, transforms and attribute
// included, so the inserted variable can never drift from the value the
// mapping evaluated. Nothing here assembles an entity expression itself.
//
// Empty until an entity is picked — there is nothing to refer to before that,
// and validateMappingRules' 'unresolved-incoming-value' check catches a result
// left holding the bare alias from that window.

const incomingValueExpression = computed(() =>
  bindingEntityId.value
    ? buildIncomingValueExpression(props.binding) : '');

const builderVariables = computed(() => {
  if (!incomingValueExpression.value) return [];
  const source = props.binding.attribute
      ? `${props.binding.entityId} · ${props.binding.attribute}`
      : `the state of ${props.binding.entityId}`;
  const transformed = (props.binding.transforms ?? []).length > 0 ? ', transformed' : '';
  return [{
    label: 'Incoming value',
    token: INCOMING_VALUE_TOKEN,
    description: `The value the rules are matched against — ${source}${transformed}`,
  }];
});

// ── Simplified on/off layout ────────────────────────────────────────────────

const isSimpleToggle = computed(() => {
  const rules = props.binding.rules;
  if (rules.length === 0) return true;
  return rules.length === 2
    && rules[0].when?.operator === '==' && rules[0].when?.value === 'on'
    && rules[1].when === null;
});

/** Lazily gives binding.rules the two-entry on/off shape this layout presents. */
function ensureToggleRules() {
  if (props.binding.rules.length < 2) {
    props.binding.rules = [
      { when: { operator: '==', value: 'on' }, result: props.binding.rules[0]?.result ?? '' },
      { when: null, result: props.binding.rules[1]?.result ?? '' },
    ];
  }
}

const onValue = computed({
  get: () => props.binding.rules[0]?.result ?? '',
  set(v) {
    ensureToggleRules();
    props.binding.rules[0].result = v;
  },
});

const offValue = computed({
  get: () => props.binding.rules[1]?.result ?? '',
  set(v) {
    ensureToggleRules();
    props.binding.rules[1].result = v;
  },
});

// ── Generic rule list ────────────────────────────────────────────────────────

/** New rule inserted before a trailing default rule, so default stays last. */
function addRule() {
  const rules = props.binding.rules;
  const insertAt = (rules.length > 0 && rules[rules.length - 1].when === null) ? rules.length - 1 : rules.length;
  rules.splice(insertAt, 0, { when: { operator: '==', value: '' }, result: '' });
}

function removeRule(index) {
  props.binding.rules.splice(index, 1);
}

function moveRule(index, delta) {
  const rules  = props.binding.rules;
  const target = index + delta;
  [rules[index], rules[target]] = [rules[target], rules[index]];
}

/** A default (when: null) rule never moves — it stays pinned last. */
function canMoveUp(index) {
  if (index === 0) return false;
  return props.binding.rules[index].when !== null;
}

function canMoveDown(index) {
  const rules = props.binding.rules;
  if (index === rules.length - 1) return false;
  if (rules[index].when === null) return false;
  if (rules[index + 1].when === null) return false; // don't push past a trailing default
  return true;
}

// ── Validation ───────────────────────────────────────────────────────────────

const issues = computed(() => validateMappingRules(props.binding.rules,
  { hasIncomingValue: Boolean(incomingValueExpression.value) }));

function issuesForRule(index) {
  return issues.value.filter(issue => issue.ruleIndex === index);
}

// Row DOM refs, keyed by rule index — rebuilt each render since rows can be
// added/removed/reordered; used only to scroll a row into view.
const ruleRowEls = ref({});
function setRowRef(index, el) {
  if (el) ruleRowEls.value[index] = el;
  else delete ruleRowEls.value[index];
}

function scrollToRule(index) {
  ruleRowEls.value[index]?.scrollIntoView({ behavior: 'smooth', block: 'center' });
}

// ── Interpolation (design-time preview toggle only, in editorState) ────────

const insufficientAnchors = computed(() => getColorAnchors(props.binding.rules).length < 2);

// ── Progressive disclosure — local, not persisted anywhere ─────────────────
// (Value Transforms' own open/closed state now lives inside
// CollapsibleTransformsSection.vue.)

const testOpen = ref(issues.value.length > 0);
</script>
